/**
 * IMPORTS
 */
import initialState from 'src/aggregates/agents/initialstate';
import Store from 'src/store';


/**
 * TYPES
 */
import * as Events from 'src/aggregates/agents/events.d';
import {IAgentReducer} from 'src/aggregates/agents/reducer.d';
import {IAgentState} from 'src/aggregates/agents/state.d';


/**
 * CODE
 */

/**
 * Agent events map.
 */
const eventsMap: IAgentReducer = {

    /**
     * I set agent status when agent have been added.
     *
     * :param state: agent state
     * :param event: agent added event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_ADDED]:
        (state: IAgentState, event: Events.IAgentAdded): IAgentState => ({
            ...state,
            byId: {
                ...state.byId,
                [event._target]: event.agent,
            },
        }),


    /**
     * I update state to add a new agent.
     *
     * :param state: agent state
     * :param event: agent-created event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_CREATED]: (
        state: IAgentState,
        event: Events.IAgentCreated,
    ): IAgentState => ({
        ...state,
        byId: {
            ...state.byId,
            [event.agent.uid]: event.agent,
        },
        hasCreateError: false,
        isCreating: false,
    }),


    /**
     * I update creating flags.
     *
     * :param state: agent state
     *
     * :returns: agent state
     */
    [Events.types.AGENT_CREATING]: (state: IAgentState): IAgentState => ({
        ...state,
        hasCreateError: false,
        isCreating: true,
    }),


    /**
     * I set agent status when agents have been loaded.
     *
     * :param state: agent state
     * :param event: agents loaded event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_LOADED]:
        (state: IAgentState, event: Events.IAgentLoaded): IAgentState => ({
            ...state,
            hasLoadError: false,
            isLoading: false,
        }),


    /**
     * I set agent status when loading agents.
     *
     * :param state: agent state
     * :param event: agents loading event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_LOADING]:
        (state: IAgentState, event: Events.IAgentLoading): IAgentState => ({
            ...state,
            hasLoadError: false,
            isLoading: true,
        }),


    /**
     * I set agent status when login metrics have been fetched.
     *
     * :param state: agent state
     * :param event: login metrics fetched event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_LOGIN_METRICS_FETCHED]: (
        state: IAgentState,
        event: Events.IAgentLoginMetricsFetched,
    ): IAgentState => ({
        ...state,
        metrics: {
            ...state.metrics,
            hasLoadError: false,
            isLoading: false,
            login: event.metrics,
        },
    }),


    /**
     * I set agent status when fetching login metrics.
     *
     * :param state: agent state
     * :param event: login metrics fetching event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_LOGIN_METRICS_FETCHING]: (
        state: IAgentState,
        event: Events.IAgentLoginMetricsFetching,
    ): IAgentState => ({
        ...state,
        metrics: {
            ...state.metrics,
            hasLoadError: false,
            isLoading: true,
        },
    }),


    /**
     * I set agent status when login metrics have not been fetched.
     *
     * :param state: agent state
     * :param event: login metrics not fetched event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_LOGIN_METRICS_NOT_FETCHED]: (
        state: IAgentState,
        event: Events.IAgentLoginMetricsNotFetched,
    ): IAgentState => ({
        ...state,
        metrics: {
            ...state.metrics,
            hasLoadError: true,
            isLoading: false,
        },
    }),


    /**
     * I set agent status when metrics period have been updated.
     *
     * :param state: agent state
     * :param event: agents metrics period set event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_METRICS_PERIOD_SET]: (
        state: IAgentState,
        event: Events.IAgentMetricsPeriodSet,
    ): IAgentState => ({
        ...state,
        metrics: {
            ...state.metrics,
            end: event.end,
            start: event.start,
        },
    }),


    /**
     * I update not-created flags.
     *
     * :param state: agent state
     *
     * :returns: agent state
     */
    [Events.types.AGENT_NOT_CREATED]: (state: IAgentState): IAgentState => ({
        ...state,
        hasCreateError: true,
        isCreating: false,
    }),


    /**
     * I set agent status when agents have not been loaded.
     *
     * :param state: agent state
     * :param event: agents not loaded event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_NOT_LOADED]:
        (state: IAgentState, event: Events.IAgentNotLoaded): IAgentState => ({
            ...state,
            hasLoadError: true,
            isLoading: false,
        }),


    /**
     * I update not-removed flags.
     *
     * :param state: agent state
     *
     * :returns: agent state
     */
    [Events.types.AGENT_NOT_REMOVED]: (state: IAgentState): IAgentState => ({
        ...state,
        hasRemoveError: true,
        isRemoving: false,
    }),


    /**
     * I update not-updated flags.
     *
     * :param state: agent state
     *
     * :returns: agent state
     */
    [Events.types.AGENT_NOT_UPDATED]: (state: IAgentState): IAgentState => ({
        ...state,
        hasUpdateError: true,
        isUpdating: false,
    }),


    /**
     * I update removed flags.
     *
     * :param state: agent state
     * :param event: agent-removed event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_REMOVED]: (
        state: IAgentState,
        event: Events.IAgentRemoved,
    ): IAgentState => ({
        ...state,
        byId: {
            ...state.byId,
            [event._target]: {
                ...state.byId[event._target],
                roles: {
                    ...state.byId[event._target].roles,
                    agent: false,
                },
            },
        },
        hasRemoveError: false,
        isRemoving: false,
    }),


    /**
     * I update removing flags.
     *
     * :param state: agent state
     *
     * :returns: agent state
     */
    [Events.types.AGENT_REMOVING]: (state: IAgentState): IAgentState => ({
        ...state,
        hasRemoveError: false,
        isRemoving: true,
    }),


    /**
     * I update state to update an agent.
     *
     * :param state: agent state
     * :param event: agent-updated event
     *
     * :returns: agent state
     */
    [Events.types.AGENT_UPDATED]: (
        state: IAgentState,
        event: Events.IAgentUpdated,
    ): IAgentState => ({
        ...state,
        byId: {
            ...state.byId,
            [event._target]: event.agent,
        },
        hasUpdateError: false,
        isUpdating: false,
    }),


    /**
     * I update updating flags.
     *
     * :param state: agent state
     *
     * :returns: agent state
     */
    [Events.types.AGENT_UPDATING]: (state: IAgentState): IAgentState => ({
        ...state,
        hasUpdateError: false,
        isUpdating: true,
    }),
};


/**
 * Agent reducer.
 */
const reducer = Store.createReducer(initialState, eventsMap);


/**
 * EXPORTS
 */
export default reducer;
